import PASentry from "util/pa-sentry";
import { deepEqual } from "../util/lodash/deepEqual";

interface ICustomConfig {
  consumeError?: boolean;
  params?: any;
  // params: { year: number; }
}

const baseURL = process.env.NEXT_PUBLIC_BASE_URI;

/**
 * Wrapper of axios,creatred via fetch method.
 * internally whole application use axios, but unhood its fetch which is being used all places
 *
 * its expose get, post, json, formData method which can be used according to requriment.
 * formData is for sending - new FormData();
 * json is for static json calls.
 */
interface IFetchResponse extends Response {
  data?: any;
}

// wait for user session to complete so, we have latest pa_id.
let isUserSessionCompleted = false;

const __handleResponse = async <T>(response: Response): Promise<T | { error: string }> => {
  const contentType = response.headers.get("Content-Type");
  if (response.url.indexOf("/user-session-v3") > 0) {
    isUserSessionCompleted = true;
  }

  const res: IFetchResponse = response;
  let data;

  try {
    if (!contentType) {
      const responeText = await response.text();
      data = responeText ? JSON.parse(responeText) : {};
    } else {
      data = await response.json();
    }
    res.data = data;
  } catch (err) {
    // Capture raw response text for debugging
    let rawResponse = "";
    try {
      const clonedResponse = response.clone();
      rawResponse = await clonedResponse.text();
    } catch (textErr) {
      rawResponse = "Unable to capture response text";
    }

    res.data = {
      error: "Failed to parse response",
      statusCode: response.status,
      statusText: response.statusText,
      rawResponse: rawResponse.substring(0, 500), // Limit to first 500 chars
      parseError: err?.message || "JSON parsing failed",
    };
  }
  if (response.ok) {
    return res;
  }
  return Promise.reject(res);
};

let __config: any;

function init(config: any) {
  if (!deepEqual(__config, config)) {
    console.log("axios config changed", config);
    __config = config;
  }
}

const client = async <T>(endpoint: string, { body, ...customConfig }: any = {}): Promise<{data: T}> => {
  let signal = {};
  let controller = {};
  const { locale = "", consumeError = true } = customConfig || {};
  const localeToPass = locale ? `${locale}` : __config?.locale || "";
  const headers = {
    "Content-Type": "application/json",
    locale: localeToPass,
  };

  const config = {
    credentials: "include",
    method: body ? "POST" : "GET",
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
  };
  if (body) {
    if (!customConfig.headers) {
      config.body = JSON.stringify(body);
    } else {
      config.body = body;
    }
  }

  let query = "";
  if (customConfig.paramsSerializer) {
    query = customConfig.paramsSerializer({ ...customConfig.params, locale: localeToPass });
  } else if (customConfig.params) {
    // eslint-disable-next-line no-param-reassign
    customConfig.params.locale = localeToPass;
    query = Object.keys(customConfig.params)
      .filter((k) => !(customConfig.params[k] === undefined || customConfig.params[k] === null))
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(customConfig.params[k])}`)
      .join("&");
  } else {
    // pasing locale in params to disable wrong locale caching on disk cache.
    query = `locale=${localeToPass}`;
  }

  // if (endpoint.startsWith("/locales")) {
  //   baseURL = process.env.NEXT_PUBLIC_ROOT_URL;
  // }
  let URL = endpoint.indexOf("http") === 0 ? endpoint : `${baseURL}${endpoint}`;

  if (query.length && URL.indexOf("?") === -1) {
    URL += `?${query}`;
  } else if (query.length) {
    URL += `&${query}`;
  }

  try {
    if (process.browser) {
      controller = new AbortController();
      signal = controller.signal;
      config.signal = signal;
    }
  } catch {}

  const apisOnHold: string[] = [];

  // these apis are dependent on pa_id which comes from user-session-v3,
  // so wait till isUserSessionCompleted
  if (process.browser && !isUserSessionCompleted && URL.indexOf("/user-session-v3") === -1 && (URL.indexOf("/api/") > 0 || URL.indexOf("/event") > 0)) {
    await new Promise((resolve) => {
      apisOnHold.push(URL);
      let interval = setInterval(() => {
        if (isUserSessionCompleted) {
          clearResources();
        }
      }, 300);
      // anyways clear timeout after 5 sec
      let timeOut = setTimeout(() => {
        if (interval) {
          // PASentry.logException(new Error(`setTimeout executed after 5 sec ${isUserSessionCompleted}`), null, { url: URL, isUserSessionCompleted, apisOnHold });
          clearResources();
        }
      }, 5500);

      function clearResources() {
        apisOnHold.splice(apisOnHold.indexOf(URL), 1);
        clearTimeout(timeOut);
        clearInterval(interval);
        interval = undefined;
        timeOut = undefined;
        resolve(true);
      }
    });
  }

  const promise = fetch(URL, config).then(__handleResponse);
  if (process.browser && controller) {
    promise.cancel = () => controller.abort();
  }

  if (consumeError && endpoint !== "/event") {
    promise.catch((err: any) => {
      console.log(`ERROR in promise URL: ${URL}`);
      const errMsg = err?.message || err?.data?.message || "";
      console.log("err.data.message", errMsg);
      let isHttpError = false;
      if (err instanceof Error && err.response) {
        // If it's an HTTP error with a response
        isHttpError = true;
        console.error(`HTTP Error: ${err.message}`);
        console.error(`Response body: ${err.response}`);
      } else {
        console.log("err", err, typeof err);
        console.log("Fetch API stringified error", JSON.stringify(err));
      }

      // Detect user offline
      const offline = typeof navigator !== "undefined" ? !navigator.onLine : false;
      const isAbort = err?.name === "AbortError" || errMsg.includes("aborted");

      // All possible "network dropped" messages
      const NETWORK_ERROR_MESSAGES = [
        "Failed to fetch",
        "NetworkError",
        "Load failed",
        "TypeError: Failed to fetch",
        "TypeError: NetworkError",
        "Network error",
        "NetworkError when attempting to fetch resource",
        "Failed to fetch (partsavatar.ca)",
        "The Internet connection appears to be offline",
        "Network request failed",
        "ERR_INTERNET_DISCONNECTED",
        "ERR_NETWORK_CHANGED",
      ];

      const isNetworkDrop = offline || isAbort
      || NETWORK_ERROR_MESSAGES.some((msg) => errMsg.includes(msg));

      if (isNetworkDrop) {
        console.log("Ignored: network drop/offline/fetch abort", {
          offline,
          isAbort,
          errMsg,
        });
        return;
      }

      // Log raw response to console for Sentry breadcrumbs if it's a parsing error
      if (err?.data?.rawResponse) {
        console.log("Raw response for failed API:", err.data.rawResponse);
        console.log("Parse error:", err.data.parseError);
      }

      // Actual API failure
      const apiEndpoint = endpoint?.split("?")[0];

      const requestBody = typeof config.body === "string"
        ? config.body
        : (config.body ? JSON.stringify(config.body) : undefined);

      PASentry.logException(
        new Error(`API FAILED - ${apiEndpoint} - ${errMsg}`),
        null,
        {
          endpoint,
          status: err?.status || err?.data?.statusCode,
          statusText: err?.statusText || err?.data?.statusText,
          httpError: isHttpError,
          err: isHttpError ? err.response : JSON.stringify(err),
          requestBody,
          queryString: query,
          offline,
          isAbort,
          errorMsg: errMsg,
        },
        [{ key: "endpoint", value: apiEndpoint }],
      );
    });
  }

  return promise;
};

function get<T>(endpoint: string, customConfig?: ICustomConfig): Promise<{data: T}> {
  const config = {
    method: "GET",
    ...customConfig,
  };
  return client(endpoint, config);
}

function post<T>(endpoint: string, body?: any, customConfig?: ICustomConfig): Promise<{data: T}> {
  const config = {
    method: "POST",
    body,
    ...customConfig,
  };
  return client(endpoint, config);
}

function json(endpoint: string) {
  return client(endpoint);
}

function formData(endpoint: string, formDataBody: any) {
  const options = {
    method: "POST",
    body: formDataBody,
  };
  const URL = endpoint.indexOf("http") === 0 ? endpoint : `${baseURL}${endpoint}`;
  return fetch(URL, options)
    .then(__handleResponse);
}

function event(endpoint: string, formDataBody: any, config: any = {}) {
  const localeToPass = __config?.locale;

  const options = {
    method: "POST",
    body: JSON.stringify(formDataBody),

    headers: {
      ...config.headers,
      locale: localeToPass,
    },
  };
  return client(endpoint, options);
}

const axios = {
  init,
  get,
  post,
  json,
  formData,
  event,
};

export default axios;

export const axiosConfig = __config;
